import React from "react";
import { useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Badge,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from "@mui/icons-material";
import { LAYOUT, COMPONENT_SIZES, SPACING } from "../../theme/constants";
import { useThemeMode } from "../../context/ThemeContext";

const menuItems = [
  { text: "Dashboard", path: "/dashboard" },
  { text: "Sites", path: "/dashboard/sites" },
  { text: "Applications", path: "/dashboard/applications" },
  { text: "Backups", path: "/dashboard/backups" },
  { text: "Analytics", path: "/dashboard/analytics" },
  { text: "Activities", path: "/dashboard/activities" },
  { text: "Plans", path: "/dashboard/plans" },
  { text: "Settings", path: "/dashboard/settings" },
  { text: "Profile", path: "/dashboard/profile" },
  { text: "Overview", path: "/admin" },
  { text: "Users", path: "/admin/users" },
  { text: "Sites", path: "/admin/sites" },
  { text: "Applications", path: "/admin/applications" },
  { text: "Containers", path: "/admin/containers" },
  { text: "Workers", path: "/admin/workers" },
  { text: "Plans", path: "/admin/plans" },
  { text: "Backups", path: "/admin/backups" },
  { text: "Activities", path: "/admin/activities" },
  { text: "Settings", path: "/admin/settings" },
];

export default function TopBar({
  drawerWidth,
  user,
  notificationCount = 0,
  onMenuClick,
  onNotificationClick,
  onProfileClick,
}) {
  const location = useLocation();
  const { toggleTheme, isDark } = useThemeMode();

  const currentPageTitle =
    menuItems.find((item) => item.path === location.pathname)?.text ||
    "Dashboard";

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { md: `calc(100% - ${LAYOUT.sidebarWidth}px)` },
        ml: { md: `${LAYOUT.sidebarWidth}px` },
        backgroundColor: "background.paper",
        color: "text.primary",
        transition: "all 0.15s ease",
        borderRadius: 0,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 48, sm: LAYOUT.topBarHeight },
          px: { xs: 1.5, sm: 2.5 },
        }}
      >
        {/* Mobile hamburger */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 1.5, display: { md: "none" } }}
          size="small"
        >
          <MenuIcon fontSize="small" />
        </IconButton>

        {/* Page title */}
        <Typography
          variant="subtitle1"
          noWrap
          component="div"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            fontSize: { xs: "0.875rem", md: "0.9375rem" },
          }}
        >
          {currentPageTitle}
        </Typography>

        {/* Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Theme Toggle */}
          <Tooltip title={isDark ? "Light mode" : "Dark mode"}>
            <IconButton
              size="small"
              color="inherit"
              onClick={toggleTheme}
              aria-label="toggle theme"
            >
              {isDark ? (
                <LightModeIcon fontSize="small" />
              ) : (
                <DarkModeIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              size="small"
              color="inherit"
              onClick={onNotificationClick}
              aria-label={`${notificationCount} notifications`}
            >
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Thin separator before avatar */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.75, height: 20, alignSelf: "center" }}
          />

          {/* Profile Avatar */}
          <Tooltip title="Profile menu">
            <IconButton
              onClick={onProfileClick}
              size="small"
              aria-haspopup="true"
              aria-label="open profile menu"
              sx={{ p: 0.5 }}
            >
              <Avatar
                sx={{
                  width: COMPONENT_SIZES.avatar.sm,
                  height: COMPONENT_SIZES.avatar.sm,
                  bgcolor: "primary.main",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                }}
              >
                {user?.firstName?.[0] || "U"}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
